import React from 'react';
import PropTypes from 'prop-types';
import { Modal, PanelGroup, Panel, designSystem } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';

const {
  ControlGroup,
  FieldStatus,
  LanguageSelectField,
  CheckboxField,
  Box,
  Stack,
  Icon,
  Tag,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  DialogHeader,
  DialogHeaderTitle,
  Link,
} = designSystem;

const FLOATING_ELEMENT_SELECTOR = '.ws-ui-base-select-option, .ws-ui-base-select__option';

function AddLanguageDialog({
  control,
  languageOptions,
  onFormSubmit,
  onCancel,
  isAiLocalizationAvailable,
  onTranslateWithAiClick,
  onTranslationGuidelinesClick,
}) {
  return (
    <Modal
      overlay="visible"
      centered
      resizable={false}
      draggable={false}
      fullscreen={false}
      width={336}
      height="auto"
      handleCancel={onCancel}
      handleSubmit={onFormSubmit}
      floatingModalElementSelector={FLOATING_ELEMENT_SELECTOR}
      className="ws-add-new-language-dialog"
    >
      <PanelGroup height="100%">
        <Panel align="middle" corners="squared" className="ws-add-new-language-dialog__header">
          <DialogHeader>
            <DialogHeaderTitle>{i18next.t('Add new language')}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel height="100%" className="ws-add-new-language-dialog__body">
          <Box
            paddingLeft="spacing-m"
            paddingRight="spacing-m"
            paddingTop="spacing-s"
            paddingBottom="spacing-s"
          >
            <Stack gap="spacing-m">
              <ControlGroup title={i18next.t('Select a new language')}>
                <LanguageSelectField
                  control={control}
                  name="locale"
                  rules={{
                    required: i18next.t('This field is required'),
                  }}
                  options={languageOptions}
                  placeholder={i18next.t('Select...')}
                  appearance="searchable"
                />

                <FieldStatus
                  text={i18next.t(
                    'All existing content and pages from the main language will be copied to the new language of your site'
                  )}
                />
              </ControlGroup>

              <Stack gap="spacing-3-xs">
                <CheckboxField
                  control={control}
                  name="translateWithAi"
                  onChange={onTranslateWithAiClick}
                  label={
                    <span className="ws-add-new-language-dialog__checkbox-label">
                      <span>{i18next.t('Translate with')}</span>
                      <Icon glyph="ai" type="accent" />
                      {!isAiLocalizationAvailable && (
                        <Tag size="small" appearance="cta-secondary" label="PRO" />
                      )}
                    </span>
                  }
                  description={
                    <span>
                      {i18next.t('To customize how text is translated, go to ')}
                      {/* eslint-disable-next-line yola/jsx-a11y/anchor-is-valid */}
                      <Link
                        href="/"
                        isUnderlined
                        onClick={(e) => {
                          e.preventDefault();
                          onTranslationGuidelinesClick();
                        }}
                      >
                        {i18next.t('Yola AI → Translation Guidelines')}
                      </Link>
                    </span>
                  }
                />
              </Stack>
            </Stack>
          </Box>
        </Panel>
        <Panel className="ws-add-new-language-dialog__footer">
          <DialogButtonGroup rounded>
            <DialogButton onClick={onFormSubmit}>{i18next.t('Submit')}</DialogButton>

            <DialogButtonGroupSeparator />

            <DialogButton stretch="block" size="large" onClick={onCancel}>
              {i18next.t('Cancel')}
            </DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
}

AddLanguageDialog.propTypes = {
  control: PropTypes.object.isRequired,
  languageOptions: PropTypes.array.isRequired,
  onFormSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isAiLocalizationAvailable: PropTypes.bool.isRequired,
  onTranslateWithAiClick: PropTypes.func.isRequired,
  onTranslationGuidelinesClick: PropTypes.func.isRequired,
};

export default AddLanguageDialog;
